import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import client from "../api/client";
import "./TaskHistory.css";

const statusTabs = ["All Tasks", "Completed", "Pending", "In Progress", "To Do"];

const parseStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const getAssignedIds = (task) => {
  if (Array.isArray(task?.assigned_employee_ids)) {
    return task.assigned_employee_ids
      .map((value) => Number(value))
      .filter(Number.isFinite);
  }

  if (typeof task?.assigned_employee_ids === "string") {
    return task.assigned_employee_ids
      .split(",")
      .map((value) => Number(value))
      .filter(Number.isFinite);
  }

  return [];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getStatusClass = (status) => {
  const normalized = String(status || "To Do").toLowerCase();
  if (normalized === "completed") return "completed";
  if (normalized === "in progress") return "in-progress";
  if (normalized === "pending") return "pending";
  return "todo";
};

const getFlowAction = (log) =>
  log?.action_type || log?.action || log?.flow_type || log?.event_type || "updated";

const getFlowMessage = (log) =>
  log?.remarks ||
  log?.message ||
  log?.comment ||
  log?.note ||
  log?.log_message ||
  "No remark provided";

const getReviewMessage = (review) =>
  review?.remarks || review?.comment || review?.note || "No remarks provided";

const getReviewAuthor = (review) =>
  review?.reviewer_name ||
  review?.employee_name ||
  review?.actor_name ||
  review?.reviewer_role ||
  "Reviewer";

export default function TaskHistory() {
  const navigate = useNavigate();
  const { employeeId, taskId } = useParams();
  const location = useLocation();
  const currentUser = useMemo(() => parseStoredUser(), []);
  const currentRole = String(currentUser?.role || "").toLowerCase();
  const canReview = ["admin", "tl"].includes(currentRole);
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState("All Tasks");
  const [query, setQuery] = useState("");
  const [notesMap, setNotesMap] = useState({});
  const [reviewsMap, setReviewsMap] = useState({});
  const [employeeFeedbackMap, setEmployeeFeedbackMap] = useState({});
  const [flowMap, setFlowMap] = useState({});
  const [attachmentsMap, setAttachmentsMap] = useState({});
const [previewImage, setPreviewImage] = useState(null);
  const [loadedDetails, setLoadedDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState({});
  const [reviewDrafts, setReviewDrafts] = useState({});
  const [savingReviewForTask, setSavingReviewForTask] = useState(null);
  const [activeReviewTask, setActiveReviewTask] = useState(null);
  const [reviewHoverRating, setReviewHoverRating] = useState(0);
  const [reviewModalError, setReviewModalError] = useState("");
  const [reviewToast, setReviewToast] = useState("");

  const fallbackName = taskId
    ? `Task ${taskId}`
    : employeeId
    ? `Employee ${employeeId}`
    : "Task";
  const headerTitle =
    location.state?.taskTitle || location.state?.employeeName || fallbackName;

  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      try {
        const res = await client.get("/tasks");
        let filtered = Array.isArray(res.data) ? res.data : [];

        if (taskId) {
          const parsedTaskId = Number(taskId);
          filtered = Number.isFinite(parsedTaskId)
            ? filtered.filter((task) => Number(task.id) === parsedTaskId)
            : [];
        } else if (employeeId) {
          const parsedEmployeeId = Number(employeeId);
          filtered = Number.isFinite(parsedEmployeeId)
            ? filtered.filter((task) => getAssignedIds(task).includes(parsedEmployeeId))
            : [];
        } else {
          filtered = [];
        }

        setTasks(filtered);
      } catch (err) {
        console.error("Fetch task history error:", err);
        alert("Failed to load task history");
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [employeeId, taskId]);

  const loadTaskDetails = async (taskIdToLoad, force = false) => {
    if (!taskIdToLoad || (!force && loadedDetails[taskIdToLoad])) {
      return;
    }

    setLoadingDetails((prev) => ({ ...prev, [taskIdToLoad]: true }));

    try {
const [
  notesRes,
  reviewsRes,
  flowRes,
  employeeFeedbackRes,
  attachmentsRes,
  followupsRes,
] = await Promise.allSettled([
  client.get(`/tasks/${taskIdToLoad}/notes`),
  client.get(`/tasks/${taskIdToLoad}/reviews`),
  client.get(`/tasks/${taskIdToLoad}/flow-logs`),
  client.get(`/tasks/${taskIdToLoad}/employee-feedback`),
  client.get(`/tasks/${taskIdToLoad}/attachments`),
  client.get(`/tasks/${taskIdToLoad}/followups`),
]);

      if (notesRes.status === "fulfilled") {
        setNotesMap((prev) => ({
          ...prev,
          [taskIdToLoad]: Array.isArray(notesRes.value.data) ? notesRes.value.data : [],
        }));
      }

      if (reviewsRes.status === "fulfilled") {
        setReviewsMap((prev) => ({
          ...prev,
          [taskIdToLoad]: Array.isArray(reviewsRes.value.data)
            ? reviewsRes.value.data
            : [],
        }));
      }

      if (flowRes.status === "fulfilled") {
        setFlowMap((prev) => ({
          ...prev,
          [taskIdToLoad]: Array.isArray(flowRes.value.data) ? flowRes.value.data : [],
        }));
      }
     if (employeeFeedbackRes.status === "fulfilled") {
  setEmployeeFeedbackMap((prev) => ({
    ...prev,
    [taskIdToLoad]: Array.isArray(employeeFeedbackRes.value.data)
      ? employeeFeedbackRes.value.data
      : [],
  }));
}
if (attachmentsRes.status === "fulfilled") {
  setAttachmentsMap((prev) => ({
    ...prev,
    [taskIdToLoad]: Array.isArray(attachmentsRes.value.data)
      ? attachmentsRes.value.data
      : [],
  }));
}
      setLoadedDetails((prev) => ({ ...prev, [taskIdToLoad]: true }));
    } catch (err) {
      console.error("Load task detail error:", err);
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [taskIdToLoad]: false }));
    }
  };

useEffect(() => {
  if (tasks.length > 0) {
    tasks.forEach((task) => {
      loadTaskDetails(task.id);
    });
  }
}, [tasks]);

  useEffect(() => {
    if (!reviewToast) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setReviewToast("");
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [reviewToast]);

  const shownTasks = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return tasks.filter((task) => {
      const tabMatch =
        activeTab === "All Tasks"
          ? true
          : String(task?.status || "").toLowerCase() === activeTab.toLowerCase();

      const searchMatch =
        !normalizedQuery ||
        task?.title?.toLowerCase().includes(normalizedQuery) ||
        task?.description?.toLowerCase().includes(normalizedQuery) ||
        task?.assigned_employees?.toLowerCase().includes(normalizedQuery) ||
        task?.category?.toLowerCase().includes(normalizedQuery);

      return tabMatch && searchMatch;
    });
  }, [tasks, activeTab, query]);

  const historyStats = useMemo(() => {
    const completed = tasks.filter(
      (task) => String(task?.status || "").toLowerCase() === "completed"
    ).length;
    const averageProgress = tasks.length
      ? Math.round(
          tasks.reduce(
            (total, task) => total + (Number(task?.progress) || 0),
            0
          ) / tasks.length
        )
      : 0;

    return [
      { label: "Total Tasks", value: tasks.length },
      { label: "Completed", value: completed },
      { label: "Average Progress", value: `${averageProgress}%` },
    ];
  }, [tasks]);

  const activeReviewTaskDetails = useMemo(
    () =>
      tasks.find((task) => Number(task.id) === Number(activeReviewTask)) || null,
    [activeReviewTask, tasks]
  );

  const activeReviewDraft = activeReviewTask
    ? reviewDrafts[activeReviewTask] || {}
    : {};

  const activeReviewSelectedRating =
    activeReviewDraft?.rating !== undefined && activeReviewDraft?.rating !== ""
      ? Number(activeReviewDraft.rating)
      : 0;

  const previewReviewRating = reviewHoverRating || activeReviewSelectedRating;

  const openReviewModal = (task) => {
    if (!task?.id) {
      return;
    }

    setReviewModalError("");
    setReviewHoverRating(0);
    setActiveReviewTask(task.id);
  };

  const closeReviewModal = () => {
    if (savingReviewForTask) {
      return;
    }

    setActiveReviewTask(null);
    setReviewHoverRating(0);
    setReviewModalError("");
  };

  const updateReviewDraft = (taskIdForDraft, field, value) => {
    setReviewDrafts((prev) => ({
      ...prev,
      [taskIdForDraft]: {
        ...prev[taskIdForDraft],
        [field]: value,
      },
    }));
  };

  const submitReview = async (task) => {
    const draft = reviewDrafts[task.id] || {};
    const rating =
      draft?.rating !== undefined && draft?.rating !== ""
        ? Number(draft.rating)
        : null;
    const remarks = String(draft?.remarks || "").trim();

    if (rating === null && !remarks) {
      setReviewModalError("Add a star rating or write remarks before submitting.");
      return;
    }

    setReviewModalError("");
    setSavingReviewForTask(task.id);
    try {
      await client.post(`/tasks/${task.id}/review`, {
        rating,
        remarks,
      });
      setReviewDrafts((prev) => ({
        ...prev,
        [task.id]: { rating: "", remarks: "" },
      }));
      setLoadedDetails((prev) => ({ ...prev, [task.id]: false }));
      await loadTaskDetails(task.id, true);
      closeReviewModal();
      setReviewToast("Successfully submitted your rating");
    } catch (err) {
      console.error("Save task review error:", err);
      setReviewModalError("Failed to save review. Please try again.");
    } finally {
      setSavingReviewForTask(null);
    }
  };

  return (
    <div className="task-history-page">
      <div className="task-history-header">
        <button onClick={() => navigate("/tasks")} className="back-btn">
          Back
        </button>
        <div>
          <h2>{headerTitle} Task History</h2>
          <p>Timeline, reassignment trail, remarks, ratings, and notes.</p>
        </div>
      </div>

      <div className="history-stat-grid">
        {historyStats.map((stat) => (
          <article key={stat.label} className="history-stat-card">
            <span>{stat.label}</span>
            <strong>{stat.value}</strong>
          </article>
        ))}
      </div>

      <div className="task-history-controls">
        <div className="task-history-tabs">
          {statusTabs.map((tab) => (
            <button
              key={tab}
              className={activeTab === tab ? "active" : ""}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={query}
          placeholder="Search tasks, category, assignee..."
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {loading ? (
        <div className="empty-history">Loading task history...</div>
      ) : shownTasks.length === 0 ? (
        <div className="empty-history">No tasks found for this filter.</div>
      ) : (
        <div className="task-history-grid">
          {shownTasks.map((task) => (
            <article
              key={task.id}
              className={`history-card ${getStatusClass(task.status)}`}
            >
              <div className="history-card-top">
                <span>{task.status || "To Do"}</span>
                <span>{task.progress || 0}%</span>
              </div>

              <div className="history-card-main">
                <div>
                  <h3>{task.title}</h3>
                  <p>{task.description || "No description"}</p>
                </div>
                <div className="history-badges">
                  <span className={`history-priority ${String(task.priority || "low").toLowerCase()}`}>
                    {task.priority || "Low"}
                  </span>
                  <span className="history-flow-badge">
                    {task.assignment_type === "tl" ? "TL Based" : "Direct"}
                  </span>
                </div>
              </div>

              <div className="history-detail-grid">
                <div>
                  <span className="history-label">Assigned To</span>
                  <strong>{task.assigned_employees || task.tl_name || "Unassigned"}</strong>
                </div>
                <div>
                  <span className="history-label">Category</span>
                  <strong>{task.category || "General"}</strong>
                </div>
                <div>
                  <span className="history-label">Task Type</span>
                  <strong>{task.task_type || "Standard"}</strong>
                </div>
                <div>
                  <span className="history-label">Deadline</span>
                  <strong>{formatDateOnly(task.deadline)}</strong>
                </div>
              </div>

              {(task.reassigned_by_name || task.reassigned_to_name || task.reassigned_at) && (
                <div className="history-meta-strip">
                  {task.reassigned_by_name ? (
                    <span>By {task.reassigned_by_name}</span>
                  ) : null}
                  {task.reassigned_to_name ? (
                    <span>To {task.reassigned_to_name}</span>
                  ) : null}
                  {task.reassigned_at ? (
                    <span>{formatDateTime(task.reassigned_at)}</span>
                  ) : null}
                </div>
              )}

              <div className="history-card-footer">
                <span>Created {formatDateOnly(task.created_at)}</span>
                <button
                  type="button"
                  className="load-timeline-btn"
                  onClick={() => loadTaskDetails(task.id, true)}
                >
                  {loadingDetails[task.id]
                    ? "Loading..."
                    : loadedDetails[task.id]
                    ? "Refresh Timeline"
                    : "Load Timeline"}
                </button>
              </div>

              <div className="history-progress">
                <div
                  className="history-progress-fill"
                  style={{ width: `${task.progress || 0}%` }}
                />
              </div>

              {loadedDetails[task.id] ? (
                <div className="history-section-stack">
                  <section className="history-section">
                    <div className="history-section-title">
                      <h4>Assignment Timeline</h4>
                    </div>
                    {flowMap[task.id]?.length ? (
                      <div className="timeline-list">
                        {flowMap[task.id].map((log, index) => (
                          <article key={log.id || `${task.id}-flow-${index}`} className="timeline-item">
                            <div className="timeline-dot" />
                            <div>
                              <strong>{getFlowAction(log)}</strong>
                              <p>{getFlowMessage(log)}</p>
                              <small>{formatDateTime(log?.created_at || log?.updated_at)}</small>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="history-empty-inline">No flow logs available yet.</p>
                    )}
                  </section>

                  <section className="history-section">
                    <div className="history-section-title">
                      <h4>Reviews & Ratings</h4>
                    </div>
                    {reviewsMap[task.id]?.length ? (
                      <div className="review-list">
                        {reviewsMap[task.id].map((review, index) => (
                          <article key={review.id || `${task.id}-review-${index}`} className="review-item">
                            <div className="review-topline">
                              <strong>{getReviewAuthor(review)}</strong>
                              <span>{review?.rating ? `${review.rating}/5` : "No rating"}</span>
                            </div>
                            <p>{getReviewMessage(review)}</p>
                            <small>{formatDateTime(review?.created_at || review?.updated_at)}</small>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="history-empty-inline">No reviews added yet.</p>
                    )}

                    {canReview ? (
                      <div className="review-form">
                        <button
                          type="button"
                          className="review-trigger-field"
                          onClick={() => openReviewModal(task)}
                        >
                          <span>Rating</span>
                          <strong>
                            {reviewDrafts[task.id]?.rating
                              ? `${reviewDrafts[task.id].rating}/5 selected`
                              : "Choose star rating"}
                          </strong>
                        </button>
                        <button
                          type="button"
                          className="notes-btn"
                          onClick={() => openReviewModal(task)}
                        >
                          Add Review
                        </button>
                      </div>
                    ) : null}
                  </section>
<section className="history-section">
  <div className="history-section-title">
    <h4>Employee Feedback</h4>
  </div>

  {employeeFeedbackMap[task.id]?.length ? (
    <div className="review-list">
      {employeeFeedbackMap[task.id].map((item, index) => (
        <article
          key={item.id || `${task.id}-employee-feedback-${index}`}
          className="review-item"
        >
          <div className="review-topline">
            <strong>Employee Feedback</strong>

            <span>
              {item?.rating ? `${item.rating}/5` : "No rating"}
            </span>
          </div>

          <p>{item?.feedback || "No feedback"}</p>

          <small>
            {formatDateTime(item?.created_at)}
          </small>
        </article>
      ))}
    </div>
  ) : (
    <p className="history-empty-inline">
      No employee feedback yet.
    </p>
  )}
</section><section className="history-section">
  <div className="history-section-title">
    <h4>Attachments</h4>
  </div>

  {attachmentsMap[task.id]?.length ? (
    <div className="review-list">
      {attachmentsMap[task.id].map((file) => (
        <div key={file.id} className="review-item">

          {file.mime_type?.startsWith("image/") && (
            <img
              src={`https://techiohisab.com/api/tasks/attachments/${file.id}/view`}
              alt={file.original_name}
              style={{
                width: "140px",
                borderRadius: "10px",
                cursor: "pointer",
                marginBottom: "10px",
              }}
              onClick={() =>
                setPreviewImage(
  `https://techiohisab.com/api/tasks/attachments/${file.id}/view`
)
              }
            />
          )}

          <strong>{file.original_name}</strong>

          <div
            style={{
              display: "flex",
              gap: "10px",
              marginTop: "10px",
            }}
          >
            <button
              className="notes-btn"
              onClick={() =>
               window.open(
  `https://techiohisab.com/api/tasks/attachments/${file.id}/view`,
  "_blank"
)
              }
            >
              View
            </button>

            <a
              href={`https://techiohisab.com/api/tasks/attachments/${file.id}/download`}
              download
              className="notes-btn"
            >
              Download
            </a>
          </div>
        </div>
      ))}
    </div>
  ) : (
    <p className="history-empty-inline">
      No attachments uploaded.
    </p>
  )}
</section>
                  <section className="history-section">
                    <div className="history-section-title">
                      <h4>Notes</h4>
                    </div>
                    {notesMap[task.id]?.length ? (
                      <div className="notes-section">
                        {notesMap[task.id].map((note, index) => (
                          <div key={note.id || `${task.id}-note-${index}`} className="note-item">
                            <strong>{note.employee_name || "Team Member"}</strong>
                            <p>{note.note || "No note text"}</p>
                            <small>{formatDateTime(note.created_at)}</small>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="history-empty-inline">No notes added yet.</p>
                    )}
                  </section>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {activeReviewTaskDetails ? (
        <div className="review-modal-backdrop" onClick={closeReviewModal}>
          <div
            className="review-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="review-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="review-modal-header">
              <div>
                <h3 id="review-modal-title">Add Review & Rating</h3>
                <p>{activeReviewTaskDetails.title || "Task Review"}</p>
              </div>
              <button
                type="button"
                className="review-modal-close"
                onClick={closeReviewModal}
                disabled={savingReviewForTask === activeReviewTaskDetails.id}
              >
                Close
              </button>
            </div>

            <div className="review-star-panel">
              <span className="review-modal-label">Star Rating</span>
              <div
                className="review-stars"
                onMouseLeave={() => setReviewHoverRating(0)}
              >
                {[1, 2, 3, 4, 5].map((star) => {
                  const active = star <= previewReviewRating;
                  return (
                    <button
                      key={star}
                      type="button"
                      className={`review-star-btn ${active ? "active" : ""}`}
                      onMouseEnter={() => setReviewHoverRating(star)}
                      onFocus={() => setReviewHoverRating(star)}
                      onClick={() =>
                        updateReviewDraft(activeReviewTaskDetails.id, "rating", String(star))
                      }
                      aria-label={`${star} star${star > 1 ? "s" : ""}`}
                    >
                      ★
                    </button>
                  );
                })}
              </div>
              <small className="review-star-caption">
                {activeReviewSelectedRating
                  ? `${activeReviewSelectedRating} out of 5 selected`
                  : "Select from 1 to 5 stars"}
              </small>
            </div>

            <label className="review-modal-textarea-wrap">
              <span className="review-modal-label">Review</span>
              <textarea
                value={activeReviewDraft?.remarks ?? ""}
                onChange={(event) =>
                  updateReviewDraft(
                    activeReviewTaskDetails.id,
                    "remarks",
                    event.target.value
                  )
                }
                placeholder="Write your feedback, performance review, or remarks..."
                disabled={savingReviewForTask === activeReviewTaskDetails.id}
              />
            </label>

            {reviewModalError ? (
              <p className="review-modal-error">{reviewModalError}</p>
            ) : null}

            <div className="review-modal-actions">
              <button
                type="button"
                className="review-cancel-btn"
                onClick={closeReviewModal}
                disabled={savingReviewForTask === activeReviewTaskDetails.id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="review-submit-btn"
                onClick={() => submitReview(activeReviewTaskDetails)}
                disabled={savingReviewForTask === activeReviewTaskDetails.id}
              >
                {savingReviewForTask === activeReviewTaskDetails.id
                  ? "Submitting..."
                  : "Submit Review"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
{previewImage && (
  <div
    className="review-modal-backdrop"
    onClick={() => setPreviewImage(null)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        maxWidth: "90%",
        maxHeight: "90%",
      }}
    >
      <img
        src={previewImage}
        alt="Preview"
        style={{
          maxWidth: "100%",
          maxHeight: "85vh",
          borderRadius: "12px",
        }}
      />
    </div> 
  </div>
)}
      {reviewToast ? (
        <div className="review-success-toast" role="status" aria-live="polite">
          <span className="review-success-icon">✓</span>
          <span>{reviewToast}</span>
        </div>
      ) : null}
    </div>
  );
}
